import { ChangeEvent, FC, Fragment } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// Models
import IReport from 'app/models/Report';
// Redux
import { useAppSelector } from 'app/hooks/useStore';
// Selectors
import { selectLoading } from 'app/store/CaseReports/CaseReports.selectors';
// Mui
import { Box, Divider, Tooltip } from '@mui/material';
// Icons
import { VisibilityOutlined as VisibilityOutlinedIcon } from '@mui/icons-material';
// Dialogs
import ContentPreviewDialog from 'app/dialogs/ContentPreviewDialog';
// Components
import { Input, Button, IconButton } from 'app/components/Mui';
// Utilities
import { isRequired } from 'app/utilities/Validations';
// Hooks
import useToggle from 'app/hooks/useToggle';
// Context
import { useReportPresetContext } from 'app/context/ReportPreset.context';
// i18next
import { useTranslation } from 'react-i18next';

type Props = {
  report?: IReport;
  onClose: () => void;
}

const DialogTitle:FC<Props> = ({
  // Props
  report, onClose
}) => {
  const { t } = useTranslation();
  // State
  const loading = useAppSelector(selectLoading);

  const { open, toggle } = useToggle();

  const { content } = useReportPresetContext();
  const { control, formState: { errors }, getValues, setValue, watch } = useFormContext();

  const removeHyperlinkTextWatcher = watch('options.removeHyperlinkText');

  return (
    <Fragment>
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        px: 6,
        py: 4
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 4
        }}>
          <Controller
            control={control} name="name"
            rules={{ required: isRequired }}
            render={({ field }) => (
              <Input
                {...field}
                sx={{ width: 351 }}
                placeholder={t('labels.name')}
                onChange={(event:ChangeEvent<HTMLInputElement>) => {
                  const isNameChanged = getValues('isNameChanged');
                  if ( !isNameChanged ) setValue('isNameChanged', true);
                  field.onChange(event.target.value);
                }}
                required
                error={Boolean(errors.name)}
                helperText={(errors.name?.message as string) || ''}
                margin="none"
                size="small"
                fullWidth={false}
              />
            )}
          />
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          {content.length ? (
            <Fragment>
              <Tooltip title={t('dialogs.reportPreset.previewButton')}>
                <IconButton
                  name="Preview report form dialog"
                  onClick={toggle}
                ><VisibilityOutlinedIcon /></IconButton>
              </Tooltip>
              <Divider sx={{ height: 32 }} orientation="vertical" />
            </Fragment>
          ) : null}
          <Button
            name="Cancel report form dialog"
            onClick={onClose}
            variant="outlined"
          >{t('buttons.close')}</Button>
          <Button
            name={`${report ? 'update' : 'create'} report form dialog`}
            type="submit"
            loading={loading}
            variant="contained"
          >{t('buttons.create')}</Button>
        </Box>
      </Box>
      {open ? (
        <ContentPreviewDialog
          open={open}
          onClose={toggle}
          content={content}
          options={{ removeHyperlinkText: removeHyperlinkTextWatcher }}
        />
      ) : null}
    </Fragment>
  )
}

export default DialogTitle;
